import { __assign, __awaiter, __generator } from "tslib";
import { useOfficeComponentContext } from '@office-start/component-context-service';
import { logError } from '@office-start/telemetry-service';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo } from 'react';
import { create } from 'zustand';
import { getCopilotPinUserData, getCopilotPinUserDataFromContext, getUserStorageOptions, setCopilotPinUserData, userCopilotFreDataDefault, } from '../utils/utilities';
var useUserCopilotFreDataStore = create(function (set) { return ({
    currentState: undefined,
    isRunning: false,
    update: function (userCopilotFreData) {
        set({
            currentState: userCopilotFreData,
        });
    },
    startUpdating: function () { return set({ isRunning: true }); },
    stopUpdating: function () { return set({ isRunning: false }); },
}); });
/**
 * Custom hook to manage user Copilot FRE data.
 *
 * @returns A tuple containing:
 *   - The current user Copilot FRE data (or undefined).
 *   - A function to update the user Copilot FRE data.
 *   - A boolean indicating if the update operation is currently running.
 */
export function useUserCopilotFreData(copilotFreDataFromOfficeContextEnabled) {
    var _this = this;
    var _a = useUserCopilotFreDataStore(), currentState = _a.currentState, updateStore = _a.update, isRunning = _a.isRunning, startUpdating = _a.startUpdating, stopUpdating = _a.stopUpdating;
    var context = useOfficeComponentContext().data;
    var update = useCallback(function (newValue) {
        // if values are equal, skip the store update
        if (isEqual(currentState, newValue)) {
            return;
        }
        updateStore(newValue);
    }, [currentState, updateStore]);
    // fetch the value
    useEffect(function () {
        if (copilotFreDataFromOfficeContextEnabled)
            return;
        var fetchData = function () { return __awaiter(_this, void 0, void 0, function () {
            var userStorageOptions, fetchedUserState, error_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!context) {
                            return [2 /*return*/];
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        userStorageOptions = getUserStorageOptions(context);
                        return [4 /*yield*/, getCopilotPinUserData(userStorageOptions)];
                    case 2:
                        fetchedUserState = _a.sent();
                        update(fetchedUserState);
                        return [3 /*break*/, 4];
                    case 3:
                        error_1 = _a.sent();
                        logError({
                            eventName: "CopiltotFre" /* KnownFeatureNames.CopilotFre */,
                            errorType: 'Copilot_Fre_Data_Error',
                            message: 'Failed to fetch user storage details for pinning copilot',
                            debugInfo: "Error for: fetchData-CopilotPinUserState info: ".concat(error_1),
                        });
                        update(userCopilotFreDataDefault);
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        }); };
        fetchData();
    }, [context, copilotFreDataFromOfficeContextEnabled, update]);
    var copilotPinUserState = useMemo(function () {
        return copilotFreDataFromOfficeContextEnabled && context
            ? getCopilotPinUserDataFromContext(context === null || context === void 0 ? void 0 : context.copilotPinUserState)
            : currentState;
    }, [context, copilotFreDataFromOfficeContextEnabled, currentState]);
    // TODO: handle multiple calls - while one is in progress, another call should wait
    var updateWrapped = useCallback(function (newState) { return __awaiter(_this, void 0, void 0, function () {
        var mergedState, userStorageOptions;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!context || !copilotPinUserState) {
                        return [2 /*return*/];
                    }
                    startUpdating();
                    mergedState = __assign(__assign({}, copilotPinUserState), newState);
                    userStorageOptions = getUserStorageOptions(context);
                    return [4 /*yield*/, setCopilotPinUserData(mergedState, userStorageOptions)];
                case 1:
                    _a.sent();
                    update(mergedState);
                    stopUpdating();
                    return [2 /*return*/];
            }
        });
    }); }, [context, copilotPinUserState, startUpdating, stopUpdating, update]);
    return {
        data: copilotPinUserState,
        update: updateWrapped,
        isRunning: isRunning,
    };
}
//# sourceMappingURL=useUserCopilotFreData.js.map